import React from 'react';
import PropTypes from 'prop-types';

const formatCurrency = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  return `$${(value / 1000).toFixed(0)}k`;
};

const TaxEfficiencyDonut = ({ breakdown, grossConsideration, className }) => {
  const safeGross = grossConsideration > 0 ? grossConsideration : 1;
  const effectiveTaxRate = ((breakdown.total_tax / safeGross) * 100).toFixed(1);

  const segments = [
    {
      label: 'Capital Gains Tax',
      amount: breakdown.capital_gains_amount,
      color: 'text-green-600 dark:text-green-500',
      bgColor: 'bg-green-600 dark:bg-green-500',
    },
    {
      label: 'Ordinary Income Tax',
      amount: breakdown.ordinary_income_amount,
      color: 'text-amber-600 dark:text-amber-500',
      bgColor: 'bg-amber-600 dark:bg-amber-500',
    },
  ];

  const grossTax =
    (breakdown.capital_gains_amount || 0) + (breakdown.ordinary_income_amount || 0);
  const hasLCGE = breakdown.lcge_offset > 0;

  return (
    <div className={`space-y-6 ${className || ''}`}>
      <div className="space-y-3">
        <div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2 font-mono uppercase tracking-wide">
            Gross Tax
          </div>
          <div className="relative h-10 rounded-[8px] overflow-hidden flex">
            {segments.map((segment) => {
              const pct = grossTax > 0 ? (segment.amount / grossTax) * 100 : 0;
              return segment.amount > 0 ? (
                <div
                  key={segment.label}
                  className={`relative ${segment.bgColor}`}
                  style={{ width: `${pct}%` }}>
                  {pct > 15 && (
                    <div className="absolute inset-0 flex items-center justify-center text-white text-[11px] font-medium">
                      {formatCurrency(segment.amount)}
                    </div>
                  )}
                </div>
              ) : null;
            })}
          </div>
        </div>

        {hasLCGE && (
          <div className="flex items-center gap-3 px-4">
            <div className="text-[13px] text-[hsl(var(--muted-foreground))]">Less: LCGE Offset</div>
            <div className="flex-1 border-b border-dashed border-[hsl(var(--border))]" />
            <div className="text-[13px] text-blue-600 dark:text-blue-500 font-medium">
              −{formatCurrency(breakdown.lcge_offset)}
            </div>
          </div>
        )}

        <div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2 font-mono uppercase tracking-wide">
            Net Tax
          </div>
          <div className="relative h-10 rounded-[8px] overflow-hidden bg-red-600 dark:bg-red-700">
            <div className="absolute inset-0 flex items-center justify-center text-white text-[13px] font-semibold">
              {formatCurrency(breakdown.total_tax)}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {segments.map(
          (segment) =>
            segment.amount > 0 && (
              <div key={segment.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-sm ${segment.bgColor}`} />
                  <div className="text-[13px] text-[hsl(var(--muted-foreground))]">{segment.label}</div>
                </div>
                <div className={`text-[13px] font-medium ${segment.color}`}>
                  {formatCurrency(segment.amount)}
                </div>
              </div>
            ),
        )}

        {hasLCGE && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-blue-600 dark:bg-blue-500" />
              <div className="text-[13px] text-[hsl(var(--muted-foreground))]">LCGE Offset</div>
            </div>
            <div className="text-[13px] font-medium text-blue-600 dark:text-blue-500">
              −{formatCurrency(breakdown.lcge_offset)}
            </div>
          </div>
        )}

        <div className="pt-3 border-t border-[hsl(var(--border))] flex items-center justify-between">
          <div className="text-[13px] text-[hsl(var(--foreground))] font-semibold">Net Tax</div>
          <div className="text-[13px] font-semibold text-red-600 dark:text-red-500">
            {formatCurrency(breakdown.total_tax)}
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-[hsl(var(--border))]">
        <div className="flex items-center justify-between">
          <div className="text-[13px] text-[hsl(var(--muted-foreground))]">Effective tax rate</div>
          <div className="text-[15px] font-semibold text-[hsl(var(--foreground))]">{effectiveTaxRate}%</div>
        </div>
      </div>
    </div>
  );
};

TaxEfficiencyDonut.propTypes = {
  breakdown: PropTypes.shape({
    capital_gains_amount: PropTypes.number,
    ordinary_income_amount: PropTypes.number,
    lcge_offset: PropTypes.number,
    total_tax: PropTypes.number,
  }).isRequired,
  grossConsideration: PropTypes.number.isRequired,
  className: PropTypes.string,
};

TaxEfficiencyDonut.defaultProps = {
  className: '',
};

export default TaxEfficiencyDonut;
